import {
  createDefaultFontSizeOptions,
  createMarkupEditorFontSizeControl,
  createMarkupEditorFontSizeOptions,
  createMarkupEditorShapeStyleControls,
  createMarkupEditorToolbar,
  createMarkupEditorToolStyle,
  createMarkupEditorToolStyles,
  createNode,
  PinturaNode,
  Sticker
} from '@pqina/pintura'
import EmojiPicker from 'components/EmojiPicker'
import { createRoot } from 'react-dom/client'
import { getRandomInRange } from 'utils/random'

/* eslint-disable no-magic-numbers */

export function configureStickerWillRenderShapePresetToolbar(containerSelector: string) {
  return (
    nodes: PinturaNode[],
    addPreset: (sticker: Sticker) => void,
    _env: any,
    _redraw: () => void
  ): PinturaNode[] => {
    const host = document.createElement('div')
    host.style.width = '354px'
    host.style.height = '436px'
    // EXPL: we must wait for the host to be added to the DOM before rendering the emoji picker
    const container = document.querySelector(containerSelector) as HTMLElement
    const observer = new MutationObserver(() => {
      if (container.contains(host)) {
        observer.disconnect()
        const root = createRoot(host)
        root.render(
          <EmojiPicker
            inline
            onSelect={(e) => {
              // calculate random x and y with 10 offset from 60 (for x) and 20 (for y)
              const x = getRandomInRange(65, 10)  
              const y = getRandomInRange(20, 10)  
              addPreset({ src: e.native, width: 100, height: 100, shape: { x: `${x}%`, y: `${y}%` } })
            }}
          />
        )
      }
    })
    observer.observe(container, { childList: true, subtree: true })
    return [...nodes, createNode('Panel', 'emoji-panel', { buttonLabel: 'All Emojis 🙂', root: host })]
  }
}


export const defaultVisibleStickers = ['🎉', '😆', '👍', '❤️', '🍕']
export const markupEditorToolbarDefault = createMarkupEditorToolbar(
  ['text', 'sharpie', 'line', 'arrow', 'rectangle', 'ellipse', 'path']
)

export const markupEditorToolStylesDefault = createMarkupEditorToolStyles({
  text: createMarkupEditorToolStyle('text', {
    // Set default text shape background to transparent
    backgroundColor: [0, 0, 0, 0],
    // Set default text shape outline to 0 width and white
    textOutline: ['0%', [1, 1, 1]],
    // Set default text shadow, shadow will not be drawn if x, y, and blur are 0.
    textShadow: ['0%', '0%', '0%', [0, 0, 0, 0.5]],  
    // Allow newlines in inline text
    disableNewline: false,
    // Align to left by default, this triggers always showing the text align control
    textAlign: 'left'
  })
})

export const defaultTargetSize = {
  width: 1920,
  height: 1080,
  fit: 'contain' as 'contain', // cast type to avoid typescript errors
  upscale: false
}

export const markupEditorShapeStyleControlsDefault = createMarkupEditorShapeStyleControls({
  fontSize: createMarkupEditorFontSizeControl(
    createMarkupEditorFontSizeOptions(createDefaultFontSizeOptions()),
    {
      enableInput: true,
      defaultValue: 48
    }
  )
})

// Helper function to calculate font size options based on image dimensions
const calculateFontSizeOptions = (imageWidth: number, imageHeight: number) => {
  // Base the calculation on the smaller dimension to ensure text fits
  const smallerDimension = Math.min(imageWidth, imageHeight)

  // Set bounds
  const minSize = Math.round(smallerDimension * 0.01)  // 1%
  const maxSize = Math.round(smallerDimension * 0.25)   // 25%

  // Create an array of 12 sizes with exponential growth
  const steps = 12
  return Array.from({ length: steps }, (_, i) => {
    // Use exponential growth formula: minSize * (growthFactor ^ step)
    // We calculate growthFactor so that the last step equals maxSize
    const growthFactor = Math.pow(maxSize / minSize, 1 / (steps - 1))
    const size = minSize * Math.pow(growthFactor, i)
    return Math.round(size)
  })
}

// Helper to determine default font size
const calculateDefaultFontSize = (imageWidth: number, imageHeight: number) => {
  const smallerDimension = Math.min(imageWidth, imageHeight)
  // Default to ~3% of smaller dimension
  return Math.round(smallerDimension * 0.03)
}

// Modified configuration factory function
export const createMarkupEditorConfig = (imageWidth: number, imageHeight: number) => {
  const fontSizeOptions = calculateFontSizeOptions(imageWidth, imageHeight)
  const defaultFontSize = calculateDefaultFontSize(imageWidth, imageHeight)

  return createMarkupEditorShapeStyleControls({
    fontSize: createMarkupEditorFontSizeControl(
      createMarkupEditorFontSizeOptions(fontSizeOptions),
      {
        enableInput: true,
        defaultValue: defaultFontSize
      }
    )
  })
}
